<template>
    <div
        class="report-base-tag"
        :class="tagClasses"
        @click="onClick"
    >
        <div
            v-if="icon && $slots.icon"
            class="report-base-tag__icon"
        >
            <slot name="icon">
                <i
                    v-if="icon.startsWith('vc-')"
                    :class="icon"
                />
                <RedesignBaseIcon
                    v-else
                    :name="icon"
                    :size="prefixIconSize"
                />
            </slot>
        </div>
        <div class="report-base-tag__content">
            <span
                class="report-base-tag__label"
                :style="maxWidth"
            >
                <slot name="label">{{ label }}</slot>
            </span>
        </div>
        <div
            v-if="$slots.actions"
            class="report-base-tag__actions"
        >
            <slot name="actions" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { convertToUnit } from '@/helpers/util'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'

const CLASS_DISABLED = 'is--disabled'
const CLASS_BORDERED = 'is--border'
const CLASS_UNSTYLED = 'is--simple'

const emit = defineEmits([ 'click' ])

interface ITagProps {
    icon?: string
    label?: string
    disabled?: boolean
    border?: boolean
    maxContent?: string | number
    unstyled?: boolean
    prefixIconSize?: number
}

const props = withDefaults(
    defineProps<ITagProps>(),
    {
        icon: '',
        label: '',
        disabled: false,
        border: false,
        unstyled: false,
        maxContent: '',
        prefixIconSize: 16
    }
)

const tagClasses = computed(() => {
    return [
        {
            [CLASS_BORDERED]: props.border,
            [CLASS_DISABLED]: props.disabled,
            [CLASS_UNSTYLED]: props.unstyled
        }
    ]
})
const maxWidth = computed(() => {
    if (!props.maxContent) {
        return ''
    }
    const size = convertToUnit(props.maxContent) as string
    return `max-width: ${size}`
})

function onClick () {
    emit('click')
}
</script>

<style lang="scss">
.report-base-tag {
    @apply inline-flex items-center justify-center gap-2 leading-none select-none max-w-full overflow-hidden;
    &:not(.is--simple) {
        @apply text-base rounded-2xl  leading-5 border border-gray-200 bg-gray-200 py-1 px-3;
        color: var(--mainTextColor-color);
    }
    &__icon, &__content, &__actions {
        @apply flex-shrink-0 inline-flex items-center;
    }
    &__label {
        @apply truncate font-medium items-center inline-block;
    }
    &.is--border {
        @apply border-ui-lines;
        background-color: var(--mainBgColor-color);
    }
    &.is--disabled {
        @apply opacity-50 pointer-events-none;
    }
}
</style>
